import { navigate, PageProps } from 'gatsby';
import React, { FC, useContext, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { BranchBlade } from '../branches/BranchBlade';
import { Button } from '../Button';
import { CalculatorsAndImageCard } from '../calculators/CalculatorsAndImageCard';
import { ExpanderPanel } from '../ExpanderPanel';
import { ExpanderPanelGroup } from '../ExpanderPanelGroup';
import { Gallery } from '../Gallery';
import HtmlContent from '../Html';
import { Download } from '../icon/icons/Download';
import { Epc } from '../icon/icons/Epc';
import { Floorplan } from '../icon/icons/Floorplan';
import { Fullscreen } from '../icon/icons/Fullscreen';
import { ImageGallery } from '../icon/icons/ImageGallery';
import { PinProperty } from '../icon/icons/PinProperty';
import { Play } from '../icon/icons/Play';
import { Print } from '../icon/icons/Print';
import { Rail } from '../icon/icons/Rail';
import { Save } from '../icon/icons/Save';
import { Saved } from '../icon/icons/Saved';
import { Underground } from '../icon/icons/Underground';
import { WhatsApp } from '../icon/icons/WhatsApp';
import { IconButton } from '../IconButton';
import { Img } from '../Img';
import { MainAndSide } from '../layouts/MainAndSide';
import { LinkList } from '../LinkList';
import { Loader } from '../Loader';
import { RegisterBlade } from '../my-kfh/RegisterBlade';
import { SavePropertyLoginOrRegisterBlade } from '../my-kfh/SavePropertyLoginOrRegisterBlade';
import { PageBackButton } from '../PageBackButton';
import { Panel } from '../Panel';
import { PillButtonGroup } from '../PillButtonGroup';
import { ArrangeAViewingBlade } from './ArrangeAViewingBlade';
import { PropertyEpcBlade } from './PropertyEpcBlade';
import { PropertyFloorplanBlade } from './PropertyFloorplanBlade';
import { PropertyMapBlade } from './PropertyMapBlade';
import PropertyMeta from './PropertyMeta';
import { PropertyPhotoGalleryBlade } from './PropertyPhotoGalleryBlade';
import PropertyPriceAndStatus from './PropertyPriceAndStatus';
import { RecentlyViewedProperties } from './RecentlyViewedProperties';
import { RequestAValuationBlade } from './RequestAValuationBlade';
import { Section } from '../Section';
import { Spacer } from '../Spacer';
import { StyledTableCell, StyledTableHeaderCell, StyledTableRow, Table } from '../Table';
import { Tag } from '../Tag';
import { Typography } from '../Typography';
import { GlobalSettingsContext } from '../../context/GlobalSettingsContext';
import { RecentlyViewedPropertiesContext } from '../../context/RecentlyViewedPropertiesContext';
import { UiStateContext } from '../../context/UiStateContext';
import { UserContext } from '../../context/UserContext';
import { useAudienceData } from '../../hooks/useAudienceData';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { useSaveProperty } from '../../hooks/useSaveProperty';
import { colors } from '../../styles/tokens/colors';
import { PropertyPagePageQueryQuery } from '../../types/graphqlTypes';
import { PROPERTY_URLS } from '../../urls';
import { createMarkup, currency, isBrowser } from '../../utils';
import { getDevelopmentSummary } from '../../utils/getDevelopmentSummary';
import { getUrlParts } from '../../utils/getUrlParts';
import { Phone } from '../icon';
import { ExperimentsContext } from '../../context/ExperimentsContext';
import { ActivePropertyContext } from '../../context/ActivePropertyContext';

// Interfaces

interface PropertySectionProps {
  heading?: string;
  children: any;
}

interface PropertySubSectionProps {
  heading?: string;
  children: any;
}

const galleryImageOptions = {
  width: 1336,
  height: 1002,
  layout: 'constrained',
  cropFocus: 'center',
  placeholder: 'none',
  quality: 80,
  backgroundColor: '#F5F5F5',
};
const gallerySmallImageOptions = {
  ...galleryImageOptions,
  width: 736,
  height: 560,
};
// Styles

const StyledMain = styled.div`
  > *:first-child {
    margin-top: 0;
  }
`;

const StyledHeading = styled(Typography)`
  ${({ theme: { space, media, maxMedia } }) => css`
    margin-bottom: ${space.md};

    @media (max-width: ${maxMedia.lg}) {
      width: calc(100% - 40px);
    }

    // Table > styles

    @media (min-width: ${media.lg}) {
      margin-bottom: ${space.lg};
    }
  `};
`;

const StyledPropertyMeta = styled(PropertyMeta)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.md};
  `};
`;

const StyledQuickLinks = styled(PillButtonGroup)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.lg};
  `};
`;

const StyledPropertySubSection = styled.div`
  ${({ theme: { space } }) => css`
    padding: ${space.lg} 0;
    & + & {
      padding-top: 0;
    }
  `};
`;

const StyledPropertySubSectionHeading = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.md};
  `};
`;

const StyledPropertySection = styled.div`
  ${({ theme: { space, borders, media } }) => css`
    margin-top: ${space.lg};
    padding-top: ${space.lg};
    border-top: ${borders.default};

    ${StyledPropertySubSection}:last-child {
      padding-bottom: 0;
    }

    ${StyledPropertySubSection}:first-of-type {
      padding-top: 0;
    }

    // Table > styles

    @media (min-width: ${media.lg}) {
      padding-top: ${space.xl};
    }
  `};
`;

const StyledPropertySectionHeading = styled(Typography)`
  ${({ theme: { space, media } }) => css`
    margin-bottom: ${space.md};

    // Table > styles

    @media (min-width: ${media.lg}) {
      margin-bottom: ${space.lg};
    }
  `};
`;

const StyleDownloadActions = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    gap: ${space.sm};

    // Table < styles

    @media (max-width: ${maxMedia.sm}) {
      flex-direction: column;
      > * {
        width: 100%;
      }
    }
  `};
`;

const StyledFeatures = styled.ul`
  ${({ theme: { media } }) => css`
    // Table > styles

    @media (min-width: ${media.lg}) {
      column-count: 2;
    }
  `};
`;

const StyledStations = styled.ul`
  ${({ theme: { space } }) => css`
    li {
      display: flex;
      align-items: center;
      gap: ${space.sm};
    }
  `};
`;

const StyledViewingPanelHeading = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.md};
  `};
`;

const StyledWhatsAppLink = styled.p`
  ${({ theme: { space } }) => css`
    margin-top: ${space.md};
    display: flex;
    align-items: center;
    gap: ${space.sm};
    height: 100%;
    border-radius: ;

    a {
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: ${space.xs};
    }
  `};
`;

const StyledVideoTourBanner = styled.div`
  ${({ theme: { space, colors, media, maxMedia, global, layers } }) => css`
    position: relative;
    height: 100%;
    border-radius: ${global.borderRadius.default};
    overflow: hidden;

    > * {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      object-fit: cover;
      transform: none;
    }

    a {
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      gap: ${space.md};
      text-decoration: none;
      color: ${colors.white.default};
      background: rgba(17, 17, 17, 0.28);
      backdrop-filter: blur(3px);
      z-index: ${layers.videoTourGalleryOverlay};
    }
  `};
`;

const StyledFloorPlan = styled.button`
  ${({ theme: { space, borders, global } }) => css`
    border: ${borders.default};
    border-radius: ${global.borderRadius.default};
    margin-bottom: ${space.lg};
    padding: ${space.lg};
    background: ${colors.white.default};
    width: 100%;
    > * {
      margin: 0 auto;
    }
    &:hover {
      border-color: ${colors.brand.default};
    }
  `};
`;

const StyledMap = styled.button`
  ${({ theme: { space, borders, global } }) => css`
    border: ${borders.default};
    border-radius: ${global.borderRadius.default};
    overflow: hidden;

    &:hover {
      border-color: ${colors.brand.default};
    }
  `};
`;

const StyledStickySidebar = styled.div`
  ${({ theme: { space, media } }) => css`
    position: sticky;
    top: ${space.lg};
    display: grid;
    gap: ${space.md};

    @media (min-width: ${media.md}) {
      grid-template-columns: repeat(2, 1fr);
      gap: ${space.lg};
    }

    @media (min-width: ${media.xl}) {
      grid-template-columns: 1fr;
    }
  `};
`;

const StyledTableShadowWrapper = styled.div`
  ${({ theme: { maxMedia, borders } }) => css`
    @media (max-width: ${maxMedia.md}) {
      position: relative;
      &:after {
        content: '';
        display: block;
        position: absolute;
        right: 0;
        top: 0;
        width: 12px;
        height: 100%;
        background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(66, 66, 66, 1) 100%);
        border-right: ${borders.default};
        opacity: 0.4;
      }
    }
  `};
`;

const StyledTableWrapper = styled.div`
  ${({ theme: { maxMedia, borders } }) => css`
    @media (max-width: ${maxMedia.md}) {
      overflow-x: auto;
      width: calc(100vw - 96px);
    }
  `};
`;

const StyledPropertyHeader = styled.div`
  ${({ theme: { space, media, layers } }) => css`
    position: relative;
  `};
`;

const StyledSaveButton = styled(IconButton)`
  ${({ theme: { space, media, layers } }) => css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${layers.propertyCardSaveButton};
  `};
`;

const StyledSavedPropertyItem = styled.span`
  ${({ theme: { space, media, maxMedia } }) => css`
    display: flex;
    align-items: center;

    @media (min-width: ${media.md}) {
      gap: ${space.sm};
    }
    @media (max-width: ${maxMedia.md}) {
      flex-direction: column;
      gap: 0;
    }
  `};
`;
const StyledSavedPropertyLabel = styled(Typography)`
  ${({ theme: { space, media, maxMedia } }) => css`
    font-weight: bold;
    @media (min-width: ${media.md}) {
      margin-top: -${space.xxs};
    }
    @media (max-width: ${maxMedia.md}) {
      margin-top: -${space.xs};
    }
  `};
`;

const StyledAudienceNavs = styled.div`
  ${({ theme: { space, media } }) => css`
    @media (min-width: ${media.md}) {
      display: grid;
      //max-width: 580px;
      grid-template-columns: 1fr 1fr;
      grid-gap: ${space.lg};
      justify-content: space-between;
    }
  `};
`;

const StyledStickyBar = styled.div`
  ${({ theme: { space, colors, layers, media } }) => css`
    width: 100%;
    position: fixed;
    bottom: 0;
    transition: top 0.3s;
    padding: ${space.md};
    background-color: ${colors.white.default};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space.sm};
    z-index: ${layers.stickyBar};
    > * {
      min-height: 66px;
    }
    button {
      flex-grow: 1;
    }
    @media (min-width: ${media.md}) {
      display: none;
    }
  `};
`;

const StyledStickyBarATag = styled.a`
  ${({ theme: { space, colors, borders, global } }) => css`
    border: ${borders.default};
    border-radius: ${global.borderRadius.default};
    padding: ${space.md};
    background: ${colors.white.default};
    cursor: pointer;
    > * {
      margin: 0 auto;
    }
    &:hover {
      border-color: ${colors.brand.default};
    }
  `};
`;

// JSX

const PropertySection: FC<PropertySectionProps> = ({ heading, children }) => (
  <StyledPropertySection>
    {heading && (
      <StyledPropertySectionHeading component="h2" variant="titleMedium">
        {heading}
      </StyledPropertySectionHeading>
    )}
    {children}
  </StyledPropertySection>
);

const PropertySubSection: FC<PropertySubSectionProps> = ({ heading, children }) => (
  <StyledPropertySubSection>
    {heading && (
      <StyledPropertySubSectionHeading component="h3" variant="titleSmall">
        {heading}
      </StyledPropertySubSectionHeading>
    )}
    {children}
  </StyledPropertySubSection>
);

const goBack = () => navigate(-1);
const PropertyDetails = ({ data, location }: PageProps<PropertyPagePageQueryQuery>) => {
  // Blade trigger elements

  const galleryBladeQuickLinkTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const floorplanBladeQuickLinkTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const floorplanBladeImageTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const floorplanBladeButtonTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const epcBladeQuickLinkTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const arrangeAViewingTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const mapBladeQuickLinkTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const mapBladeImageTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const mapBladeButtonTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const branchDetailsTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const requestAValuationTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;

  const audienceData = useAudienceData();
  const { property } = data;
  const { primaryCategory, firstImage, propertyAge } = property;
  const prevPageType = location?.state?.prevPageType;

  const { backButtonUrl, backButtonLabel, backButtonOnClick } = useMemo(() => {
    // Back button state
    let setBackButtonUrl: string | undefined = `/${PROPERTY_URLS.searchResults}`;
    let setBackButtonLabel = 'all properties';
    let setBackButtonOnClick: () => Promise<void> | undefined;

    if (primaryCategory === 'development' || property?.development) {
      setBackButtonUrl = `/${PROPERTY_URLS.developmentProperties}`;
      setBackButtonLabel = 'all developments';
      // Check if prevPath is in location state and the prevPath was a property-listing template
    } else if (propertyAge === 'New home') {
      setBackButtonUrl = `/${PROPERTY_URLS.newHomeProperties}`;
      setBackButtonLabel = 'all new homes';
    } else if (prevPageType === 'property-listing') {
      setBackButtonUrl = undefined;
      setBackButtonOnClick = () => goBack();
      setBackButtonLabel = 'search results';
    }

    return {
      backButtonUrl: setBackButtonUrl,
      backButtonLabel: setBackButtonLabel,
      backButtonOnClick: setBackButtonOnClick,
    };
  }, [primaryCategory, prevPageType, propertyAge]);

  const { dataLayerPush } = useDataLayerPush();
  const { recentlyViewedProperties, setRecentlyViewedProperties } = useContext(RecentlyViewedPropertiesContext);
  const { setActiveProperty } = useContext(ActivePropertyContext);
  const { activeBlade, setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);
  const { siteSettings } = useContext(GlobalSettingsContext);

  const { user, setRegisterData } = useContext(UserContext);
  const { isSaved, handleSaveProperty, savingLoading, deleteLoading } = useSaveProperty(
    property.uri,
    property?.seo?.h1,
    property
  );

  // Categorisation

  const isDevelopment = property.primaryCategory === 'development';
  const isCommercial = property.commercial;
  const isRental = property.primaryCategory === 'rental';

  // Images and Floorplans

  const galleryImagesSmall = isDevelopment ? property?.images?.developmentImages : property?.images?.photographsSmall;
  const galleryImages = isDevelopment ? property?.images?.developmentImages : property?.images?.photographs;
  const smallFloorplans = property?.images?.floorplansSmall;
  const floorplanSmallImageData = smallFloorplans && smallFloorplans.length > 0 ? smallFloorplans[0] : null;

  // Handle Blades

  const handleOpenBlade = (bladeId, triggerEl) => {
    setActiveBlade(bladeId);
    setActiveBladeTriggerEl(triggerEl);
  };

  // Linked location

  const locationImages =
    property?.area?.mediaSet?.media?.length > 0
      ? property?.area?.mediaSet?.media
      : property?.area?.region?.mediaSet?.media;

  const locationImageData = locationImages?.length >= 1 ? locationImages[0]?.media : null;

  // Plots - Bedroom / type logic
  // Range of bedrooms and plot types available

  const availablePlots = property?.plots || property?.relatedPlots;
  const { plotBedrooms, summaryText } = getDevelopmentSummary(availablePlots);

  // Specifications
  // Grouping by category

  const specificationsCategories = [];
  if (property.specifications && property.specifications.length > 0) {
    property.specifications.forEach(({ category }) => {
      if (!specificationsCategories.includes(category)) {
        specificationsCategories.push(category);
      }
    });
  }

  // Add this property to recently viewed properties context

  const updatedRecentlyViewedProperties = recentlyViewedProperties;

  // Check if current property is NOT in current array and the current property is not a development

  if (
    !isDevelopment &&
    isBrowser &&
    !updatedRecentlyViewedProperties?.some(
      (updatedRecentlyViewedProperty) => updatedRecentlyViewedProperty.id === property.id
    )
  ) {
    // If there is more than 3 items then remove the last items

    if (updatedRecentlyViewedProperties && updatedRecentlyViewedProperties?.length > 5) {
      updatedRecentlyViewedProperties.pop();
    }

    // Add new item to start of array

    updatedRecentlyViewedProperties?.unshift(property);
    setRecentlyViewedProperties(updatedRecentlyViewedProperties);
    localStorage.setItem('recentlyViewedProperties', JSON.stringify(updatedRecentlyViewedProperties));
  }

  // Handle saving property

  const handleClickSaveProperty = () => {
    // Check if user is logged in

    // if yes then handle saving property

    if (user) {
      handleSaveProperty();
    } else {
      setRegisterData({
        action: 'saveProperty',
        data: {
          propertyUri: property.uri,
        },
      });
      // Else open login/Register blade
      setActiveBlade('savePropertyLoginOrRegister');
    }
  };

  // Audiance content
  const audienceContentHeading = !isRental ? audienceData?.forSale?.heading : audienceData?.rental?.heading;
  const audienceContentLead = !isRental ? audienceData?.forSale?.lead : audienceData?.rental?.lead;
  const audienceContentItems = !isRental ? audienceData?.forSale?.linkLists : audienceData?.rental?.linkLists;

  const { experiments } = useContext(ExperimentsContext);

  return (
    <>
      {/* Back button */}

      <PageBackButton url={backButtonUrl} label={backButtonLabel} onClick={backButtonOnClick} />

      {/* Gallery */}

      <Section contained>
        <Gallery property={property}>
          {galleryImagesSmall?.map((media, index) => {
            if (index === 2 && property.virtualViewingsUrl) {
              return (
                <StyledVideoTourBanner key={`gallery-${index}`}>
                  <a
                    href={property.virtualViewingsUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      dataLayerPush({
                        event: 'video tour',
                        clickText: 'click to open video tour',
                        clickBladeId: undefined,
                        ...getUrlParts(property.virtualViewingsUrl),
                      })
                    }
                  >
                    <Play color="brand" size="large" />
                    <Typography component="span" variant="titleSmall">
                      Click to open video tour
                    </Typography>
                  </a>
                  <Img
                    src={media}
                    alt={`${property.address.town} ${property.address.outwardCode}`}
                    options={gallerySmallImageOptions}
                    size="827x552"
                  />
                </StyledVideoTourBanner>
              );
            }
            return (
              <button
                key={`gallery-${index}`}
                onClick={() => setActiveBlade('propertyPhotoGallery')}
                title="Expand images"
                type="button"
              >
                <Img
                  src={media}
                  alt={`${property.address.town} ${property.address.outwardCode}`}
                  options={gallerySmallImageOptions}
                  size="827x552"
                />
              </button>
            );
          })}

          {/*  Replace image used for virtual tour at end */}

          {galleryImages[2] && property.virtualViewingsUrl ? (
            <button onClick={() => setActiveBlade('propertyPhotoGallery')} title="Expand images" type="button">
              <Img
                src={galleryImages[2]}
                alt={`${property.address.town} ${property.address.outwardCode}`}
                options={gallerySmallImageOptions}
                size="827x552"
              />
            </button>
          ) : null}
        </Gallery>
      </Section>

      {/* Main and side section */}

      <Section contained>
        <MainAndSide>
          {/* Main */}
          <StyledMain>
            {!isDevelopment ? (
              <StyledPropertyHeader>
                {/* Heading */}
                <StyledHeading component="h1" variant="displaySmall">
                  {createMarkup(property?.seo?.h1)}
                </StyledHeading>

                {/* Property Price and status */}
                <PropertyPriceAndStatus property={property} />

                {/* Property meta */}
                <StyledPropertyMeta property={property} />

                {/* Save property */}
                <StyledSaveButton a11yTitle="Save property" onClick={() => handleClickSaveProperty()}>
                  {savingLoading || deleteLoading ? (
                    <Loader size="medium" />
                  ) : (
                    <>
                      {isSaved ? (
                        <>
                          <StyledSavedPropertyItem>
                            <Saved size="medium" />
                            <StyledSavedPropertyLabel component="span" variant="bodySmall">
                              Saved
                            </StyledSavedPropertyLabel>
                          </StyledSavedPropertyItem>
                        </>
                      ) : (
                        <>
                          <StyledSavedPropertyItem>
                            <Save size="medium" />
                            <StyledSavedPropertyLabel component="span" variant="bodySmall">
                              Save
                            </StyledSavedPropertyLabel>
                          </StyledSavedPropertyItem>
                        </>
                      )}
                    </>
                  )}
                </StyledSaveButton>
              </StyledPropertyHeader>
            ) : (
              <StyledPropertyHeader>
                {/* Heading */}
                <StyledHeading component="h1" variant="displaySmall">
                  {createMarkup(property?.seo?.h1)}
                </StyledHeading>
                {property?.developmentMinPrice && (
                  <StyledHeading component="h2" variant="titleLarge">
                    {currency(property?.developmentMinPrice) === currency(property?.developmentMaxPrice) ? (
                      <>{currency(property?.developmentMinPrice)}</>
                    ) : (
                      <>
                        {currency(property?.developmentMinPrice)} - {currency(property?.developmentMaxPrice)}
                      </>
                    )}
                  </StyledHeading>
                )}
                {summaryText && (
                  <Typography component="p" variant="bodyLarge">
                    {summaryText}
                  </Typography>
                )}
              </StyledPropertyHeader>
            )}

            {/* Quick links */}

            <StyledQuickLinks>
              {/* Floorplan */}

              {property?.images?.floorplans && property?.images?.floorplans.length > 0 ? (
                <Button
                  variant="pill"
                  onClick={() => handleOpenBlade('propertyFloorplan', floorplanBladeQuickLinkTriggerEl)}
                  type="button"
                  ref={floorplanBladeQuickLinkTriggerEl}
                  iconLeft={<Floorplan />}
                >
                  Floorplan
                </Button>
              ) : (
                ''
              )}

              {/* Photos */}

              {galleryImages && galleryImages.length > 0 ? (
                <>
                  <Button
                    variant="pill"
                    onClick={() => handleOpenBlade('propertyPhotoGallery', galleryBladeQuickLinkTriggerEl)}
                    type="button"
                    ref={galleryBladeQuickLinkTriggerEl}
                    iconLeft={<ImageGallery />}
                  >
                    Photos
                  </Button>
                  <PropertyPhotoGalleryBlade
                    photographs={galleryImages}
                    hasFloorPlan={property?.images?.floorplans && property?.images?.floorplans.length > 0}
                  />
                </>
              ) : (
                ''
              )}

              {/* Video tour */}

              {property && property.virtualViewings && property.virtualViewingsUrl ? (
                <Button variant="pill" url={property.virtualViewingsUrl} iconLeft={<Play />} target="_blank">
                  Video tour
                </Button>
              ) : (
                ''
              )}

              {/* Map */}

              <Button
                variant="pill"
                onClick={() => handleOpenBlade('propertyMap', mapBladeQuickLinkTriggerEl)}
                type="button"
                ref={mapBladeQuickLinkTriggerEl}
                iconLeft={<PinProperty />}
              >
                View on map
              </Button>

              {/* EPC */}

              {property?.images?.epc && property?.images?.epc.length > 0 ? (
                <>
                  <Button
                    variant="pill"
                    onClick={() => handleOpenBlade('propertyEpc', epcBladeQuickLinkTriggerEl)}
                    type="button"
                    ref={epcBladeQuickLinkTriggerEl}
                    iconLeft={<Epc />}
                  >
                    EPC
                  </Button>
                  <PropertyEpcBlade epcs={property.images.epc} />
                </>
              ) : (
                ''
              )}
            </StyledQuickLinks>

            {/* Description and details*/}

            {property.description || property.additionalDescription ? (
              <PropertySection heading={isDevelopment ? 'Development details' : 'Description'}>
                <PropertySubSection>
                  <HtmlContent>
                    {property.salesDetails && (
                      <>
                        <p>{createMarkup(property.salesDetails)}</p>
                      </>
                    )}
                    {property.description && <p>{createMarkup(property.description)}</p>}
                    {property.additionalDescription && <p>{createMarkup(property.additionalDescription)}</p>}
                    {property.development && property.development?.description ? (
                      <p>{createMarkup(property.development?.description)}</p>
                    ) : (
                      ''
                    )}
                  </HtmlContent>
                </PropertySubSection>
                {property.location && (
                  <PropertySubSection heading="Location">
                    <p>{createMarkup(property.location)}</p>
                  </PropertySubSection>
                )}
                {property.businessRates && (
                  <PropertySubSection heading="Business rates">
                    <p>{createMarkup(property.businessRates)}</p>
                  </PropertySubSection>
                )}
                {property.rentDetails && (
                  <PropertySubSection heading="Rent details">
                    <p>{createMarkup(property.rentDetails)}</p>
                  </PropertySubSection>
                )}
                {property.termsAndConditions && (
                  <PropertySubSection heading="Terms and conditions">
                    <Typography component="small" variant="smallprint">
                      {createMarkup(property.termsAndConditions)}
                    </Typography>
                  </PropertySubSection>
                )}

                {/* Features */}

                {property.features && property.features.length > 0 ? (
                  <PropertySubSection heading="Features">
                    <HtmlContent>
                      <StyledFeatures>
                        {property.features.map((feature, index) => (
                          <li key={index}>{feature.description}</li>
                        ))}
                      </StyledFeatures>
                    </HtmlContent>
                  </PropertySubSection>
                ) : (
                  ''
                )}

                {/* Brochure and print actions */}

                <StyleDownloadActions>
                  {property.propertyBrochure && (
                    <Button
                      variant="outline"
                      url={property.propertyBrochure}
                      target="_blank"
                      rel="noreferrer"
                      iconRight={<Download />}
                    >
                      Download brochure
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => isBrowser && window.print()}
                    type="button"
                    iconRight={<Print />}
                  >
                    Print details
                  </Button>
                </StyleDownloadActions>
              </PropertySection>
            ) : (
              ''
            )}

            {/* Floorplan */}

            {property?.images?.floorplans && property?.images?.floorplans.length > 0 ? (
              <PropertySection heading="Floorplan">
                <StyledFloorPlan
                  onClick={() => handleOpenBlade('propertyFloorplan', floorplanBladeImageTriggerEl)}
                  type="button"
                  ref={floorplanBladeImageTriggerEl}
                >
                  {floorplanSmallImageData && (
                    <Img
                      src={floorplanSmallImageData}
                      options={{
                        height: 360,
                        layout: 'constrained',
                        cropFocus: 'center',
                        placeholder: 'none',
                        quality: 80,
                        backgroundColor: '#F5F5F5',
                      }}
                      size="1120x748"
                    />
                  )}
                </StyledFloorPlan>
                <Button
                  variant="outline"
                  iconRight={<Fullscreen />}
                  onClick={() => handleOpenBlade('propertyFloorplan', floorplanBladeButtonTriggerEl)}
                  type="button"
                  ref={floorplanBladeButtonTriggerEl}
                >
                  Enlarge Floorplan
                </Button>
                <PropertyFloorplanBlade floorplans={property?.images?.floorplans} />
              </PropertySection>
            ) : (
              ''
            )}

            {/* Plots  / Other plots*/}

            {plotBedrooms.length > 0 && (
              <PropertySection heading={property?.relatedPlots ? 'Other plots' : 'Plots'}>
                <StyledPropertySubSection>
                  <Spacer as="ul">
                    {plotBedrooms.sort().map((bedrooms, index) => {
                      const bedroomPlots = availablePlots.filter((plot) => plot.bedrooms === bedrooms);

                      let bedroomsTitle;
                      if (bedrooms === 0) {
                        bedroomsTitle = `Studio`;
                      } else {
                        bedroomsTitle = `${bedrooms} bedroom${bedrooms !== 1 ? 's' : ''}`;
                      }

                      bedroomPlots.sort((a, b) => parseInt(a.plotNumber) - parseInt(b.plotNumber));

                      return (
                        <ExpanderPanel open title={bedroomsTitle} key={index} as="li">
                          <StyledTableShadowWrapper>
                            <StyledTableWrapper>
                              <Table>
                                <thead>
                                  <StyledTableRow sticky>
                                    <StyledTableHeaderCell sticky>Plot no.</StyledTableHeaderCell>
                                    <StyledTableHeaderCell>Floor no.</StyledTableHeaderCell>
                                    <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                                    <StyledTableHeaderCell>Status</StyledTableHeaderCell>
                                    <StyledTableHeaderCell>Price</StyledTableHeaderCell>
                                    <StyledTableHeaderCell />
                                  </StyledTableRow>
                                </thead>
                                <tbody>
                                  {bedroomPlots.map((plot, i) => {
                                    const isAvailable = plot.sellingState === 'For sale';
                                    return (
                                      <StyledTableRow key={i}>
                                        <StyledTableCell sticky>{plot.plotNumber}</StyledTableCell>
                                        <StyledTableCell>{plot.floorLevel}</StyledTableCell>
                                        <StyledTableCell>{plot.propertyType}</StyledTableCell>
                                        <StyledTableCell>
                                          <Tag
                                            text={isAvailable ? 'Available' : plot.sellingState}
                                            backgroundColor={isAvailable ? 'valid' : 'black'}
                                          />
                                        </StyledTableCell>
                                        <StyledTableCell>{currency(plot.salesPrice)}</StyledTableCell>
                                        <StyledTableCell>
                                          <Button variant="pill" arrowRight url={plot.relativeUrl}>
                                            Details
                                          </Button>
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </StyledTableWrapper>
                          </StyledTableShadowWrapper>
                        </ExpanderPanel>
                      );
                    })}
                  </Spacer>
                </StyledPropertySubSection>
                {property.development && property.development.relativeUrl ? (
                  <StyledPropertySubSection>
                    <Button variant="outline" url={property.development.relativeUrl} arrowRight>
                      Development details
                    </Button>
                  </StyledPropertySubSection>
                ) : (
                  ''
                )}
              </PropertySection>
            )}

            {/* Specification */}

            {specificationsCategories.length > 0 && (
              <PropertySection heading="Specification">
                <ExpanderPanelGroup as="ul">
                  {specificationsCategories.sort().map((category, index) => {
                    const specList = property.specifications.filter((spec) => spec.category === category);
                    return (
                      <ExpanderPanel title={category} key={index} as="li">
                        <HtmlContent>
                          <ul>
                            {specList.map((spec, i) => (
                              <li key={i}>{spec.detail}</li>
                            ))}
                          </ul>
                        </HtmlContent>
                      </ExpanderPanel>
                    );
                  })}
                </ExpanderPanelGroup>
              </PropertySection>
            )}

            {/* Location */}

            <PropertySection heading="Location">
              {property.staticMap &&
                property.latitude &&
                property.longitude &&
                property.latitude !== 0 &&
                property.longitude !== 0 && (
                  <Spacer>
                    <StyledMap
                      type="button"
                      onClick={() => handleOpenBlade('propertyMap', mapBladeImageTriggerEl)}
                      ref={mapBladeImageTriggerEl}
                    >
                      <Img src={property.staticMap} />
                    </StyledMap>

                    <Button
                      variant="outline"
                      iconRight={<Fullscreen />}
                      type="button"
                      onClick={() => handleOpenBlade('propertyMap', mapBladeButtonTriggerEl)}
                      ref={mapBladeButtonTriggerEl}
                    >
                      Enlarge map
                    </Button>
                  </Spacer>
                )}
              <PropertyMapBlade
                latitude={property?.latitude}
                longitude={property.longitude}
                id={property.id}
                title={`${property.address.town} ${property.address.outwardCode}`}
                category={property.primaryCategory.toUpperCase()}
                available={property.available}
                branch={property.branch}
              />
              {property.stations && property.stations.length > 0 ? (
                <PropertySubSection heading="Nearest stations">
                  <StyledStations>
                    {property.stations.map((station, index) => (
                      <li key={index}>
                        {station.trainStationType === 'TUBE' ? <Underground /> : <Rail />}
                        {station.distance} miles {station.name}
                      </li>
                    ))}
                  </StyledStations>
                </PropertySubSection>
              ) : (
                ''
              )}

              {property.area && (
                <>
                  <PropertySubSection heading="Local area">
                    {property.area.guideExcerpt ? (
                      <HtmlContent>{property.area.guideExcerpt}</HtmlContent>
                    ) : (
                      <HtmlContent>
                        Discover what it's really like to live in {property.area.name} with information including local
                        amenities, architecture, outdoor space, transport and local schools.
                      </HtmlContent>
                    )}
                  </PropertySubSection>
                  {property.area.relativeUrl && (
                    <Button url={property.area.relativeUrl} variant="outline" arrowRight>
                      View {property.area.name} area guide
                    </Button>
                  )}
                </>
              )}

              {/* Letting disclaimer */}

              {isRental && (
                <PropertySubSection heading="Disclaimer">
                  <Typography component="span" variant="smallprint">
                    {createMarkup(siteSettings.lettingsDisclaimer)}
                  </Typography>
                </PropertySubSection>
              )}
            </PropertySection>

            {/* Audience content */}
            {!isDevelopment && !isCommercial ? (
              <PropertySection dontPrint heading={audienceContentHeading}>
                <Spacer>
                  <HtmlContent>{audienceContentLead}</HtmlContent>
                  <StyledAudienceNavs>
                    {audienceContentItems.map(({ items, smallHeading }, index) => (
                      <LinkList key={index} items={items} smallHeading={smallHeading} />
                    ))}
                  </StyledAudienceNavs>
                </Spacer>
              </PropertySection>
            ) : (
              ''
            )}
          </StyledMain>

          {/* Side */}
          <div>
            <StyledStickySidebar>
              <Panel
                heading={property.available ? 'Arrange a viewing' : property.sellingState}
                headingBorder
                border
                buttonOne={
                  property.available
                    ? {
                        label: 'Arrange a viewing',
                        variant: 'solid',
                        type: 'button',
                        onClick: () => {
                          setActiveProperty(property);
                          handleOpenBlade('propertyArrangeAViewing', arrangeAViewingTriggerEl);
                        },
                        ref: arrangeAViewingTriggerEl,
                        disableClickTracking: true,
                      }
                    : {}
                }
                buttonTwo={
                  property.branch
                    ? {
                        label: 'Branch details',
                        onClick: () => handleOpenBlade('branch', branchDetailsTriggerEl),
                        type: 'button',
                        variant: 'outline',
                        ref: branchDetailsTriggerEl,
                      }
                    : {}
                }
              >
                {isCommercial && (
                  <>
                    <StyledViewingPanelHeading component="h4" variant="titleSmall">
                      {siteSettings.commercialTeamName}
                    </StyledViewingPanelHeading>
                    <p>
                      <strong>Call agent</strong>{' '}
                      <a href={`tel:${siteSettings.commercialPhone.replace(/\s/g, '')}`}>
                        {siteSettings.commercialPhone}
                      </a>
                    </p>
                    <p>
                      <a href={`mailto:${siteSettings.commercialEmail}}`}>{siteSettings.commercialEmail}</a>
                    </p>
                  </>
                )}
                {property.branch && (
                  <>
                    <StyledViewingPanelHeading component="h4" variant="titleSmall">
                      KFH {property.branch.name}
                    </StyledViewingPanelHeading>
                    <p>
                      <strong>Call agent</strong>{' '}
                      <a
                        href={`tel:${
                          isRental
                            ? property?.branch?.businessProfile?.lettingsContactDetails.telephone.replace(/\s/g, '')
                            : property?.branch?.businessProfile?.salesContactDetails.telephone.replace(/\s/g, '')
                        }`}
                      >
                        {isRental
                          ? property?.branch?.businessProfile?.lettingsContactDetails.telephone
                          : property?.branch?.businessProfile?.salesContactDetails.telephone}
                      </a>
                    </p>
                    <p>
                      <a
                        href={`mailto:${
                          isRental
                            ? property?.branch?.businessProfile?.lettingsContactDetails.email
                            : property?.branch?.businessProfile?.salesContactDetails.email
                        }`}
                      >
                        {isRental
                          ? property?.branch?.businessProfile?.lettingsContactDetails.email
                          : property?.branch?.businessProfile?.salesContactDetails.email}
                      </a>
                    </p>
                    {siteSettings.arrangeViewingCTASendBeeApi &&
                    property.available &&
                    property?.whatsApp?.whatsAppEnabled ? (
                      <StyledWhatsAppLink>
                        <strong>Message us on</strong>
                        <a
                          target="_blank"
                          href={property.whatsApp?.whatsAppFullUrl}
                          rel="noreferrer"
                          onClick={() =>
                            dataLayerPush({
                              event: 'whatsapp viewing click',
                              clickBladeID: undefined,
                              clickText: 'Message us on WhatsApp',
                              ...getUrlParts(property.whatsApp?.whatsAppFullUrl as string),
                            })
                          }
                        >
                          <WhatsApp size="medium" />
                          WhatsApp
                        </a>
                      </StyledWhatsAppLink>
                    ) : (
                      ''
                    )}
                  </>
                )}
              </Panel>
              <Panel
                heading="Need a valuation?"
                border
                buttonOne={{
                  label: 'Request a valuation',
                  onClick: () => handleOpenBlade('propertyRequestAValuation', requestAValuationTriggerEl),
                  variant: 'outline',
                  ref: requestAValuationTriggerEl,
                }}
              >
                <Typography component="h3" variant="lead">
                  Our local agents can provide an accurate valuation for your property.
                </Typography>
              </Panel>
            </StyledStickySidebar>
          </div>
        </MainAndSide>
      </Section>
      {/* Calculators and Area guide image card */}
      {!isRental && (
        <CalculatorsAndImageCard
          purchasePrice={property.salesPrice}
          imageCard={
            property?.area?.name && property?.area?.region?.name
              ? {
                  title: `${property?.area?.name} <br /> ${property?.area?.region?.name}`,
                  image: locationImageData,
                  tag: 'Guide',
                  link: {
                    url: property?.area?.relativeUrl,
                    name: 'Learn more',
                  },
                }
              : null
          }
        />
      )}

      {/* Recently Viewed properties */}
      <RecentlyViewedProperties usedIn="recently viewed carousel property detail" />

      {property?.lastUpdated && (
        <div style={{ display: 'none' }} data-test="updated" id="proerty-updated">
          U: {property.lastUpdated} - C:{property.createdDate}
        </div>
      )}

      {/* Blades */}

      {property.branch && activeBlade === 'branch' ? (
        <BranchBlade branch={property.branch} prioritiseLettings={isRental} />
      ) : (
        ''
      )}
      <RegisterBlade />
      <RequestAValuationBlade />

      <StyledStickyBar>
        {property.branch && (
          <>
            <StyledStickyBarATag
              href={`tel:${
                isRental
                  ? property?.branch?.businessProfile?.lettingsContactDetails.telephone.replace(/\s/g, '')
                  : property?.branch?.businessProfile?.salesContactDetails.telephone.replace(/\s/g, '')
              }`}
            >
              <Phone size="default" />
            </StyledStickyBarATag>
          </>
        )}
        {isCommercial && (
          <>
            <StyledStickyBarATag href={`tel:${siteSettings.commercialPhone.replace(/\s/g, '')}`}>
              <Phone size="default" />
            </StyledStickyBarATag>
          </>
        )}

        {property.available && property?.whatsApp?.whatsAppEnabled ? (
          <StyledStickyBarATag
            target="_blank"
            href={property.whatsApp?.whatsAppFullUrl}
            rel="noreferrer"
            onClick={() =>
              dataLayerPush({
                event: 'whatsapp viewing click',
                clickBladeID: undefined,
                clickText: 'Message us on WhatsApp',
                ...getUrlParts(property.whatsApp?.whatsAppFullUrl as string),
              })
            }
          >
            <WhatsApp size="default" />
          </StyledStickyBarATag>
        ) : (
          ''
        )}
        {property.available ? (
          <Button
            label="Arrange a viewing"
            variant="solid"
            type="button"
            onClick={() => {
              setActiveProperty(property);
              handleOpenBlade('propertyArrangeAViewing', arrangeAViewingTriggerEl);
            }}
            ref={arrangeAViewingTriggerEl}
          />
        ) : (
          ''
        )}
      </StyledStickyBar>
    </>
  );
};

export default PropertyDetails;
